import { Erc4626ReadAbi, TotalSupplyAbi } from '../abis/shared'
import { TreehouseFastlaneAbi, TreehouseRedemptionAbi } from '../abis/treehouse'
import type { LstRegistryEntry } from '../registry'
import type { VaultExitRoute } from '../../exitRoutes'
import { ONE_E18, toBigInt, type LstReader } from './shared'

/** Both modules scale their fee on 1e4 (`PRECISION` in the sources), so the
 *  raw value already IS basis points. */
const asBps = (v: bigint | undefined): number | undefined =>
  v === undefined ? undefined : Number(v)

const fmt = (raw: bigint | undefined, decimals: number): number | undefined =>
  raw === undefined ? undefined : Number(raw) / 10 ** decimals

/**
 * Treehouse tAsset (tETH / tAVAX / tHYPE) — an ERC-4626 share whose exit
 * lives on two sibling contracts, and whose two legs disagree on every
 * number that matters.
 *
 * Nine calls: the three standard 4626 reads on the share token, then the
 * live dials of each exit leg —
 *   Fastlane:     `getRedeemableAmount`, `redemptionFee`, `minRedeemInUnderlying`
 *   RedemptionV2: `waitingPeriod`, `redemptionFee`, `minRedeemInUnderlying`
 *
 * Every one of those six is an owner dial, and four of them contradict
 * something: the docs describe one 7-day wait while the chain answers 7 d /
 * 17 d / 10 d; the docs say 0.5 % for the fast leg (right) and imply the
 * queued leg is free (wrong — it charges 5 bps); and the queued leg carries a
 * MINIMUM, which inverts the usual reading of `fee-or-queued`. Pinning any of
 * them in the package would publish a number that is wrong somewhere today.
 *
 * Why not the generic `erc4626` reader:
 *
 *  - **`liquidity` would read 0.** The orchestrator's idle-balance fallback
 *    measures `underlying.balanceOf(shareToken)`, but a tAsset holds nothing
 *    — the underlying sits in the `Vault` and the payout is made by the
 *    `RedemptionController` on the Fastlane's behalf. `getRedeemableAmount()`
 *    is the only honest answer and it already nets off the underlying
 *    earmarked by pending queued requests.
 *  - **The generic route derivation would be wrong in three places** — it
 *    would report the queued leg free, with no minimum, and would attribute
 *    no minimum to the instant leg either.
 *
 * `totalAssets` / `convertToAssets` are denominated in the internal
 * accounting unit, which is minted 1:1 against the underlying — hence usable
 * verbatim as underlying amounts. The registry pins the real underlying
 * (`Vault.getUnderlying()`); `asset()` must never be used.
 */
export const readerTreehouseTAsset = (entry: LstRegistryEntry): LstReader => {
  const fastlane = entry.readerExtras?.treehouseFastlane
  const redemption = entry.readerExtras?.treehouseRedemption
  // Both periphery targets fall back to the share token when the extra is
  // missing, which fails under `allowFailure` and degrades to "dials
  // unknown" rather than dropping a live row.
  const fl = fastlane ?? entry.address
  const rd = redemption ?? entry.address
  const underlyingDec = entry.underlyingDecimals ?? entry.decimals

  return {
    calls: [
      { address: entry.address, name: 'totalAssets', params: [] },
      { address: entry.address, name: 'totalSupply', params: [] },
      { address: entry.address, name: 'convertToAssets', params: [ONE_E18] },
      { address: fl, name: 'getRedeemableAmount', params: [] },
      { address: fl, name: 'redemptionFee', params: [] },
      { address: fl, name: 'minRedeemInUnderlying', params: [] },
      { address: rd, name: 'waitingPeriod', params: [] },
      { address: rd, name: 'redemptionFee', params: [] },
      { address: rd, name: 'minRedeemInUnderlying', params: [] },
    ],
    abis: [
      Erc4626ReadAbi,
      TotalSupplyAbi,
      Erc4626ReadAbi,
      TreehouseFastlaneAbi,
      TreehouseFastlaneAbi,
      TreehouseFastlaneAbi,
      TreehouseRedemptionAbi,
      TreehouseRedemptionAbi,
      TreehouseRedemptionAbi,
    ],
    parse: (slice) => {
      const [
        assets,
        supply,
        rate,
        redeemable,
        fastFee,
        fastMin,
        waiting,
        queueFee,
        queueMin,
      ] = slice
      const totalAssets = toBigInt(assets)
      const totalSupply = toBigInt(supply)
      const exchangeRate = toBigInt(rate)
      if (
        totalAssets === undefined ||
        totalSupply === undefined ||
        exchangeRate === undefined
      ) {
        return undefined
      }

      // The buffer can legitimately be 0 — the Fastlane returns 0 rather than
      // reverting once pending redemptions outrun the vault balance — so an
      // `undefined` here means the CALL failed, not "no liquidity".
      const liquidity = toBigInt(redeemable) ?? 0n
      const wait = toBigInt(waiting)
      const instantFeeBps = asBps(toBigInt(fastFee))
      const queuedFeeBps = asBps(toBigInt(queueFee))
      const instantMin = toBigInt(fastMin)
      const queuedMin = toBigInt(queueMin)

      const instant: VaultExitRoute = {
        id: 'instant',
        kind: 'instant',
        label: 'Fast redemption',
        settlement: 'sync',
        feeBps: instantFeeBps,
        waitSeconds: 0,
        // `0` is the live value on all three chains and means "no floor" —
        // emitted rather than dropped, because on this protocol the ABSENCE
        // of a minimum on the expensive leg is the reason it is the only exit
        // most holders have.
        minAmount: instantMin?.toString(),
        minAmountFormatted: fmt(instantMin, underlyingDec),
        capacity: liquidity.toString(),
        capacityFormatted: Number(liquidity) / 10 ** underlyingDec,
        selfOnly: true,
        description:
          'Burns the tAsset and pays the underlying in the same transaction, out of the vault balance not already earmarked by queued requests. Bounded by that balance — a larger redemption reverts rather than partially filling.',
      }

      const queued: VaultExitRoute = {
        id: 'queued',
        kind: 'queued',
        label:
          wait !== undefined
            ? `${Math.round(Number(wait) / 86_400)}-day queue`
            : 'Queued redemption',
        settlement: 'async',
        feeBps: queuedFeeBps,
        waitSeconds: wait !== undefined ? Number(wait) : undefined,
        minAmount: queuedMin?.toString(),
        minAmountFormatted: fmt(queuedMin, underlyingDec),
        selfOnly: true,
        description:
          'Escrows the shares now and pays after the wait. Far cheaper, but it enforces a minimum size, and it settles at the WORSE of the rate at request and at claim — so the amount quoted when you file it is a ceiling, not a quote.',
      }

      return {
        totalAssets,
        totalSupply,
        exchangeRate,
        liquidity,
        withdrawalCooldownSeconds: wait !== undefined ? Number(wait) : undefined,
        withdrawFeeBps: instantFeeBps,
        // Cheapest reachable leg first is the list's ordering rule, and here
        // the cheap one is second on purpose: it is gated by a minimum that
        // most holders do not clear, so the leg anyone can take leads.
        exitRoutes: [instant, queued],
      }
    },
  }
}
